import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, Paper, Alert, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Lottie from 'react-lottie-player/dist/LottiePlayerLight';
import { loginUser, verifyOtp, clearError, resetOtpFlow } from '../store/slices/authSlice';
import HeaderThank from '../components/Thanks/HeaderThank';
import { flowLogger } from '../utils/flowLogger';

const OTP_PENDING_KEY = 'baoubab-otp-pending';

const readOtpPending = () => {
  try {
    const raw = sessionStorage.getItem(OTP_PENDING_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeOtpPending = (pendingEmail) => {
  sessionStorage.setItem(
    OTP_PENDING_KEY,
    JSON.stringify({ email: pendingEmail, otpPending: true })
  );
};

const clearOtpPending = () => {
  sessionStorage.removeItem(OTP_PENDING_KEY);
};

const Authpage = () => {
  const [step, setStep] = useState('login'); // 'login' ou 'verify'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [emailError, setEmailError] = useState('');
  const [lottieAnimation, setLottieAnimation] = useState(null);
  const [backgroundLoaded, setBackgroundLoaded] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Pré-charger l'image de fond
  useEffect(() => {
    const img = new Image();
    img.src = '/assets/backgroundOne.png';
    img.onload = () => setBackgroundLoaded(true);
  }, []);

  // Charger l'animation Lottie
  useEffect(() => {
    const loadLottie = async () => {
      try {
        const response = await fetch('/json_file/Livre ouvert.json');
        const data = await response.json();
        setLottieAnimation(data);
      } catch (error) {
        console.error('Erreur lors du chargement de l\'animation Lottie:', error);
      }
    };
    loadLottie();
  }, []);

  const {
    isAuthenticated,
    loading,
    error,
    user
  } = useSelector((state) => state.auth);

  // Restaurer l'étape OTP après refresh si un code est en attente
  useEffect(() => {
    const pending = readOtpPending();
    if (pending?.otpPending && pending.email) {
      setEmail(pending.email);
      setStep('verify');
      flowLogger('auth.otp.step', { restored: true, email: pending.email });
    }
  }, []);

  // Redirection si déjà authentifié
  useEffect(() => {
    if (isAuthenticated && user) {
      clearOtpPending();
      navigate('/HomePage');
    }
  }, [isAuthenticated, user, navigate]);

  const showVerifyStep = step === 'verify';

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Gestion de la soumission du formulaire de connexion
  const handleLoginSubmit = async (e) => {
    e.preventDefault();

    if (!validateEmail(email)) {
      setEmailError('Veuillez entrer une adresse email valide');
      return;
    }

    setEmailError('');
    dispatch(clearError());

    const normalizedEmail = email.trim().toLowerCase();
    setEmail(normalizedEmail);
    flowLogger('auth.login.start', { email: normalizedEmail });

    try {
      await dispatch(loginUser({ email: normalizedEmail })).unwrap();
      setStep('verify');
      writeOtpPending(normalizedEmail);
      flowLogger('auth.login.success', { email: normalizedEmail });
    } catch (error) {
      clearOtpPending();
      flowLogger('auth.login.fail', { email: normalizedEmail, error: String(error) });
      console.error('Erreur lors de l\'envoi OTP:', error);
    }
  };

  // Gestion de la vérification OTP
  const handleOtpSubmit = async (e) => {
    e.preventDefault();

    if (!otp || otp.length !== 6) {
      return;
    }

    const normalizedEmail = email.trim().toLowerCase();

    try {
      const result = await dispatch(verifyOtp({ email: normalizedEmail, otp })).unwrap();
      if (result.data && result.tokens) {
        clearOtpPending();
        flowLogger('auth.verify.success', { email: normalizedEmail });
        navigate('/HomePage');
      }
    } catch (error) {
      flowLogger('auth.verify.fail', { email: normalizedEmail, error: String(error) });
      console.error('Erreur lors de la vérification OTP:', error);
    }
  };

  // Recommencer le processus (type="button" obligatoire — sinon submit du form OTP)
  const handleRetry = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    dispatch(resetOtpFlow());
    clearOtpPending();
    setStep('login');
    setOtp('');
    setEmail('');
    setEmailError('');
    flowLogger('auth.otp.step', { restored: false, action: 'retry' });
  };

  return (
    <Box
      className="mobile-app-container"
      sx={{
        height: '100vh',
        width: '100%',
        backgroundImage: backgroundLoaded ? 'url(/assets/backgroundOne.png)' : 'none',
        backgroundSize: 'cover',
        backgroundColor:'rgba(65, 160, 201, 0.7)',
        backgroundPosition: 'center',
        overflow: 'hidden',
        position: 'relative',
        paddingTop: '6px',
        transition: 'background-image 0.3s ease-in-out'
      }}
    >
      <HeaderThank />

      {/* Section principale avec formulaire */}
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: 'calc(100vh - 100px)',
        p: { xs: 2, sm: 3 },
        marginTop: { xs: '-15px', sm: '-25px' },
        gap: { xs: 2, sm: 3 }
      }}>
        {/* Animation Lottie de bienvenue */}
        <Box sx={{
          height: { xs: '100px', sm: '150px' },
          width: { xs: '100px', sm: '150px' },
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
          {lottieAnimation ? (
            <Lottie
              animationData={lottieAnimation}
              loop
              play
              style={{ width: '100%', height: '100%' }}
            />
          ) : (
            <Box sx={{
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white'
            }}>
              <CircularProgress color="inherit" size={40} />
            </Box>
          )}
        </Box>

        {/* Formulaire d'authentification */}
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, sm: 4 },
            width: '100%',
            maxWidth: '400px',
            borderRadius: { xs: '12px', sm: '15px' },
            backgroundColor: 'rgba(255, 255, 255, 0.95)'
          }}
        >
          <Typography
            variant="h5"
            sx={{
              textAlign: 'center',
              mb: 3,
              fontWeight: 'bold',
              color: '#784e3c',
              fontFamily: 'Achiko',
              fontSize: { xs: '1.25rem', sm: '1.5rem' }
            }}
          >
            {showVerifyStep ? 'Vérification OTP' : 'Connexion à baoubab'}
          </Typography>

          {/* Affichage des erreurs */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!showVerifyStep ? (
            // Formulaire de connexion
            <form onSubmit={handleLoginSubmit}>
              <TextField
                fullWidth
                label="Adresse email"
                type="email"
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={!!emailError}
                helperText={emailError}
                disabled={loading}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '15px'
                  }
                }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading || !email}
                sx={{
                  backgroundColor: '#784e3c',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.95rem', sm: '1.1rem' },
                  textTransform: 'none',
                  borderRadius: '50px',
                  py: { xs: 1.2, sm: 1.5 },
                  boxShadow: '0px 4px 10px rgba(181, 129, 54, 0.4)',
                  '&:hover': {
                    backgroundColor: '#B58136',
                    boxShadow: '0px 6px 15px rgba(181, 129, 54, 0.6)',
                  },
                  '&:disabled': {
                    backgroundColor: '#ccc',
                    color: '#666'
                  }
                }}
              >
                {loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  'Recevoir le code de vérification'
                )}
              </Button>
            </form>
          ) : (
            <>
            <form onSubmit={handleOtpSubmit}>
              <Typography
                variant="body2"
                sx={{
                  textAlign: 'center',
                  mb: 2,
                  color: '#666'
                }}
              >
                Un code de vérification a été envoyé à <strong>{email}</strong>
              </Typography>

              <TextField
                fullWidth
                label="Code de vérification (6 chiffres)"
                variant="outlined"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                disabled={loading}
                inputProps={{
                  maxLength: 6,
                  style: { textAlign: 'center', fontSize: '1.2rem' }
                }}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '15px'
                  }
                }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading || otp.length !== 6}
                sx={{
                  backgroundColor: '#784e3c',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  textTransform: 'none',
                  borderRadius: '50px',
                  py: 1.5,
                  boxShadow: '0px 4px 10px rgba(181, 129, 54, 0.4)',
                  '&:hover': {
                    backgroundColor: '#B58136',
                    boxShadow: '0px 6px 15px rgba(181, 129, 54, 0.6)',
                  },
                  '&:disabled': {
                    backgroundColor: '#ccc',
                    color: '#666'
                  }
                }}
              >
                {loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  'Vérifier le code'
                )}
              </Button>
            </form>
            <Button
              type="button"
              variant="text"
              fullWidth
              onClick={handleRetry}
              disabled={loading}
              sx={{
                mt: 2,
                color: '#666',
                textDecoration: 'underline',
                minHeight: 48,
                touchAction: 'manipulation',
              }}
            >
              Utiliser une autre adresse email
            </Button>
            </>
          )}
        </Paper>

        {/* Informations supplémentaires */}
        <Typography
          variant="body2"
          sx={{
            textAlign: 'center',
            color: 'white',
            maxWidth: '400px',
            lineHeight: 1.6
          }}
        >
          {showVerifyStep
            ? 'Saisissez le code à 6 chiffres reçu par email pour vérifier votre identité.'
            : 'Entrez votre adresse email pour recevoir un code de vérification et accéder à votre espace parent.'
          }
        </Typography>
      </Box>
    </Box>
  );
};

export default Authpage;